import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

import exportService from '../../services/exportService';
import useModernAlert from '../../hooks/useModernAlert';
import {
  ExportConfig,
  ExportFields,
  ExportField,
  ExportPreset,
  InvoiceFilters,
  ExportRequest
} from '../../types/export';
import czechTranslations from '../../translations/czech';
import EnhancedFilterBuilder from './EnhancedFilterBuilder';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  currentFilters?: InvoiceFilters;
  selectedInvoiceIds?: number[];
  onExportComplete?: () => void;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  currentFilters = {},
  selectedInvoiceIds = [],
  onExportComplete
}) => {
  const { showError, showSuccess, AlertComponent } = useModernAlert();
  
  // State
  const [loading, setLoading] = useState(false);
  const [exportFields, setExportFields] = useState<ExportFields | null>(null);
  const [presets, setPresets] = useState<ExportPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<ExportPreset | null>(null);
  const [filterTemplates, setFilterTemplates] = useState<any>(null);
  const [businessUnits, setBusinessUnits] = useState<Array<{ id: number; name: string; ico?: string; dic?: string }>>([]);
  
  // Export configuration
  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    filters: currentFilters,
    selectedFields: [],
    exportFormat: 'excel',
    fileName: 'invoices'
  });

  // Preset management
  const [showPresetForm, setShowPresetForm] = useState(false);
  const [presetForm, setPresetForm] = useState({
    name: '',
    description: '',
    isDefault: false
  });

  // Load data on mount
  useEffect(() => {
    if (open) {
      loadExportData();
    }
  }, [open]);

  // Load export fields and presets
  const loadExportData = async () => {
    try {
      setLoading(true);
      const [fieldsResponse, presetsResponse, filterTemplatesResponse] = await Promise.all([
        exportService.getExportFields(),
        exportService.getExportPresets(),
        exportService.getFilterTemplates()
      ]);

      setExportFields(fieldsResponse.fields);
      setPresets(presetsResponse.presets);
      setFilterTemplates(filterTemplatesResponse.filterTemplates);
      setBusinessUnits(filterTemplatesResponse.businessUnits);
      
      // Set default field selection
      if (fieldsResponse.defaultSelection.length > 0) {
        setExportConfig(prev => ({
          ...prev,
          selectedFields: fieldsResponse.defaultSelection
        }));
      }

      // Set default preset if exists
      const defaultPreset = presetsResponse.presets.find(p => p.isDefault);
      if (defaultPreset) {
        setSelectedPreset(defaultPreset);
        setExportConfig(prev => ({
          ...prev,
          filters: { ...prev.filters, ...defaultPreset.filters },
          selectedFields: defaultPreset.selectedFields,
          exportFormat: defaultPreset.exportFormat,
          fileName: defaultPreset.name
        }));
      }
    } catch (error) {
      showError('Nepodařilo se načíst export data');
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      setLoading(true);
      
      const exportRequest: ExportRequest = {
        filters: exportConfig.filters,
        selectedFields: exportConfig.selectedFields,
        exportFormat: exportConfig.exportFormat,
        fileName: exportConfig.fileName,
        presetId: selectedPreset?.id
      };

      if (selectedInvoiceIds.length > 0) {
        await exportService.exportSelectedInvoices(selectedInvoiceIds, exportRequest);
      } else {
        await exportService.exportInvoices(exportRequest);
      }

      showSuccess('Export byl úspěšně dokončen');
      onExportComplete?.();
      onClose();
    } catch (error) {
      showError('Export se nezdařil');
    } finally {
      setLoading(false);
    }
  };

  // Handle preset selection
  const handlePresetSelect = (preset: ExportPreset) => {
    setSelectedPreset(preset);
    setExportConfig(prev => ({
      ...prev,
      filters: { ...prev.filters, ...preset.filters },
      selectedFields: preset.selectedFields,
      exportFormat: preset.exportFormat,
      fileName: preset.name
    }));
  };

  // Handle field selection
  const handleFieldToggle = (fieldValue: string) => {
    setExportConfig(prev => ({
      ...prev,
      selectedFields: prev.selectedFields.includes(fieldValue)
        ? prev.selectedFields.filter(f => f !== fieldValue)
        : [...prev.selectedFields, fieldValue]
    }));
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: InvoiceFilters) => {
    setExportConfig(prev => ({
      ...prev,
      filters: newFilters
    }));
  };

  // Handle preset save
  const handleSavePreset = async () => {
    try {
      setLoading(true);
      
      const presetData = {
        name: presetForm.name,
        description: presetForm.description,
        filters: exportConfig.filters,
        selectedFields: exportConfig.selectedFields,
        exportFormat: exportConfig.exportFormat,
        isDefault: presetForm.isDefault
      };

      await exportService.createExportPreset(presetData);
      
      showSuccess('Preset byl úspěšně uložen');
      setShowPresetForm(false);
      setPresetForm({ name: '', description: '', isDefault: false });
      loadExportData(); // Reload presets
    } catch (error) {
      showError('Nepodařilo se uložit preset');
    } finally {
      setLoading(false);
    }
  };

  // Handle preset delete
  const handleDeletePreset = async (presetId: number) => {
    try {
      await exportService.deleteExportPreset(presetId);
      showSuccess('Preset byl úspěšně smazán');
      loadExportData(); // Reload presets
      
      if (selectedPreset?.id === presetId) {
        setSelectedPreset(null);
      }
    } catch (error) {
      showError('Nepodařilo se smazat preset');
    }
  };

  // Handle preset duplicate
  const handleDuplicatePreset = async (preset: ExportPreset) => {
    try {
      const newName = `${preset.name} (kopie)`;
      await exportService.duplicateExportPreset(preset.id, newName);
      showSuccess('Preset byl úspěšně duplikován');
      loadExportData(); // Reload presets
    } catch (error) {
      showError('Nepodařilo se duplikovat preset');
    }
  };

  // Handle set default preset
  const handleSetDefaultPreset = async (presetId: number) => {
    try {
      await exportService.setDefaultPreset(presetId);
      showSuccess('Výchozí preset byl aktualizován');
      loadExportData(); // Reload presets
    } catch (error) {
      showError('Nepodařilo se nastavit výchozí preset');
    }
  };

  // Render field selection
  const renderFieldSelection = () => {
    if (!exportFields) return null;

    return Object.entries(exportFields).map(([category, fields]: [string, ExportField[]]) => (
      <Accordion key={category} defaultExpanded={category === 'basic'}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            {getCategoryLabel(category)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            {fields.map((field) => (
              <Grid item xs={12} sm={6} key={field.value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportConfig.selectedFields.includes(field.value)}
                      onChange={() => handleFieldToggle(field.value)}
                    />
                  }
                  label={field.label}
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    ));
  };

  // Get category label
  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      basic: 'Základní informace',
      supplier: 'Dodavatel',
      customer: 'Odběratel',
      financial: 'Finanční údaje',
      business: 'Obchodní údaje',
      system: 'Systémové údaje'
    };
    return labels[category] || category;
  };

  // Render preset management
  const renderPresetManagement = () => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Export presets</Typography>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => setShowPresetForm(true)}
        >
          Uložit jako preset
        </Button>
      </Box>

      {/* Preset list */}
      <Stack spacing={1}>
        {presets.map((preset) => (
          <Box
            key={preset.id}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: selectedPreset?.id === preset.id ? 'primary.main' : 'divider',
              borderRadius: 1,
              backgroundColor: selectedPreset?.id === preset.id ? 'primary.light' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: selectedPreset?.id === preset.id ? 'primary.light' : 'action.hover'
              }
            }}
            onClick={() => handlePresetSelect(preset)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {preset.name}
                  </Typography>
                  {preset.isDefault && (
                    <Chip
                      label="Výchozí"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                {preset.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {preset.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={preset.exportFormat.toUpperCase()}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${preset.selectedFields.length} polí`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Duplikovat">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicatePreset(preset);
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Nastavit jako výchozí">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDefaultPreset(preset.id);
                    }}
                  >
                    {preset.isDefault ? <BookmarkIcon color="primary" /> : <BookmarkBorderIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Smazat">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePreset(preset.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Save preset form */}
      {showPresetForm && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Uložit jako nový preset
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Název presetu"
                value={presetForm.name}
                onChange={(e) => setPresetForm(prev => ({ ...prev, name: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Popis (volitelné)"
                value={presetForm.description}
                onChange={(e) => setPresetForm(prev => ({ ...prev, description: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={presetForm.isDefault}
                    onChange={(e) => setPresetForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                  />
                }
                label="Nastavit jako výchozí"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSavePreset}
                  disabled={!presetForm.name.trim()}
                >
                  Uložit
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowPresetForm(false)}
                >
                  Zrušit
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileDownloadIcon />
            <Typography variant="h6">Export faktur</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Left column - Export configuration */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Konfigurace exportu
                </Typography>

                {/* Export format */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Formát exportu</InputLabel>
                  <Select
                    value={exportConfig.exportFormat}
                    onChange={(e) => setExportConfig(prev => ({
                      ...prev,
                      exportFormat: e.target.value as 'excel' | 'csv'
                    }))}
                    label="Formát exportu"
                  >
                    <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>

                {/* File name */}
                <TextField
                  fullWidth
                  label="Název souboru"
                  value={exportConfig.fileName}
                  onChange={(e) => setExportConfig(prev => ({
                    ...prev,
                    fileName: e.target.value
                  }))}
                  sx={{ mb: 2 }}
                />

                {/* Selected fields count */}
                <Alert severity="info" sx={{ mb: 2 }}>
                  Vybráno {exportConfig.selectedFields.length} polí pro export
                </Alert>

                {/* Export button */}
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                  disabled={exportConfig.selectedFields.length === 0}
                  sx={{ mb: 2 }}
                >
                  Exportovat faktury
                </Button>

                {/* Selected invoices info */}
                {selectedInvoiceIds.length > 0 && (
                  <Alert severity="warning">
                    Exportujete {selectedInvoiceIds.length} vybraných faktur
                  </Alert>
                )}
              </Grid>

                             {/* Right column - Field selection and presets */}
               <Grid item xs={12} md={6}>
                 <Typography variant="h6" sx={{ mb: 2 }}>
                   Výběr polí
                 </Typography>
                 
                 {renderFieldSelection()}
               </Grid>

               {/* Full width - Enhanced filters */}
               <Grid item xs={12}>
                 <Divider sx={{ my: 2 }} />
                 {filterTemplates && businessUnits.length > 0 && (
                   <EnhancedFilterBuilder
                     filters={exportConfig.filters}
                     onFiltersChange={handleFiltersChange}
                     businessUnits={businessUnits}
                     filterTemplates={filterTemplates}
                   />
                 )}
               </Grid>

              {/* Full width - Preset management */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                {renderPresetManagement()}
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Zavřít
          </Button>
        </DialogActions>
      </Dialog>

      {AlertComponent}
    </>
  );
};

export default ExportDialog;
